import type { CheckoutPath } from '../checkout/types'
import { hashEmail } from '../utils/cryptoUtils'
import { sendGtmEvent } from './gtmUtils'

const getTrialLengthFromItems = (items: MembershipPlanItem[]): number | undefined => {
  for (const item of items) {
    if (item.trialLengthDays) return item.trialLengthDays
  }
  return undefined
}

const mapMembershipItems = (items: MembershipPlanItem[]) => {
  return items.map((item) => {
    // item_id and item_bundle are already normalised to the recurring base SKU
    // by the caller; trial length comes from price metadata via trialLengthDays.
    const trialLength = item.trialLengthDays
    return {
      ...item,
      item_brand: 'ZOE',
      item_category: 'Membership',
      item_category2: 'Zoe Plus',
      ...(trialLength && { item_category5: String(trialLength) }),
    }
  })
}

type TrackPDPViewEventProps = {
  currency: string
  user_id?: string
  user_email?: string
}

export const trackMembershipPDPViewEvent = ({ currency, user_id, user_email }: TrackPDPViewEventProps) => {
  const event = {
    event: 'view_item',
    section: 'pdp',
    page_name: 'pdp',
    product_type: 'membership',
    product_selected: 'membership',
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: [
        {
          item_id: 'membership_pdp',
          item_name: 'Membership (PDP)',
          item_brand: 'ZOE',
          item_category: 'Membership',
        },
      ],
    },
    ...((user_id || user_email) && { user_id: user_id || hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

export const trackGutHealthTestPDPViewEvent = ({ currency, user_email }: TrackPDPViewEventProps) => {
  const event = {
    event: 'view_item',
    section: 'pdp',
    page_name: 'pdp',
    product_type: 'tests',
    product_selected: 'gut-health-test',
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: [
        {
          item_id: 'tests_pdp',
          item_name: 'Gut Health Test (PDP)',
          item_brand: 'ZOE',
          item_category: 'Tests',
          item_category2: 'Gut Health Test',
        },
      ],
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

export type MembershipPlanItem = {
  item_bundle: string
  item_id: string
  item_name: string
  item_category3: string
  item_category4: string
  price: number
  discount?: number
  quantity: number
  /** Explicit trial length in days — sourced from price metadata or a known constant.
   * When set, item_id is already normalised to the recurring base SKU by the caller
   * (no suffix to strip). Preferred over suffix parsing in getTrialLengthFromItems. */
  trialLengthDays?: number
}

type TrackMembershipPlansViewEventProps = {
  currency: string
  items: MembershipPlanItem[]
  user_email?: string
}

export const trackMembershipPlansViewEvent = ({ currency, items, user_email }: TrackMembershipPlansViewEventProps) => {
  const event = {
    event: 'view_item_list',
    section: 'plans',
    page_name: 'plans',
    product_type: 'membership',
    product_selected: 'membership',
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: mapMembershipItems(items),
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type TrackMembershipPlanSelectEventProps = {
  currency: string
  items: MembershipPlanItem[]
  user_email?: string
  listId?: string
}

export const trackMembershipPlanSelectEvent = ({
  currency,
  items,
  user_email,
  listId,
}: TrackMembershipPlanSelectEventProps) => {
  const trialLength = getTrialLengthFromItems(items)
  const event = {
    event: 'select_item',
    section: 'plans',
    page_name: 'plan_selected',
    product_type: 'membership',
    product_selected: items[0]?.item_bundle || items[0]?.item_id,
    ...(trialLength && { order_trial_length: trialLength }),
    ecommerce: {
      currency: currency?.toUpperCase(),
      ...(listId && { item_list_id: listId, item_list_name: listId }),
      items: mapMembershipItems(items),
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type TrackGutHealthTestSelectEventProps = {
  currency: string
  user_email?: string
  country?: string
  price?: number
}

export const trackGutHealthTestSelectEvent = ({
  currency,
  user_email,
  country,
  price = 149,
}: TrackGutHealthTestSelectEventProps) => {
  const event = {
    event: 'select_item',
    section: 'plans',
    page_name: 'plan_selected',
    product_type: 'tests',
    product_selected: 'GB_GUT_MICROBIOME_TEST',
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: [
        {
          item_id: 'GB_GUT_MICROBIOME_TEST',
          item_name: 'Gut Health Test',
          item_brand: 'ZOE',
          item_category: 'Tests',
          item_category2: 'Gut Health Test',
          item_category3: 'One time payment',
          item_category4: country,
          price: price,
          quantity: 1,
        },
      ],
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type TrackBeginMembershipCheckoutEventProps = {
  user_type: 'New' | 'Existing' | 'Guest'
  currency: string
  items: MembershipPlanItem[]
  user_id?: string
  user_email?: string
  checkout_path?: CheckoutPath
}

export const trackBeginMembershipCheckoutEvent = ({
  user_type,
  currency,
  items,
  user_email,
  checkout_path,
}: TrackBeginMembershipCheckoutEventProps) => {
  const trialLength = getTrialLengthFromItems(items)
  const event = {
    event: 'begin_checkout',
    user_type: user_type,
    section: 'checkout',
    page_name: 'checkout',
    ...(checkout_path && { checkout_path }),
    product_type: 'membership',
    product_selected: items[0]?.item_bundle || items[0]?.item_id,
    ...(trialLength && { order_trial_length: trialLength }),
    ecommerce: {
      currency: currency?.toUpperCase(),
      items: mapMembershipItems(items),
    },
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type UserData = {
  first_name: string
  last_name: string
  email: string
  phone_number: string
  country: string
  city: string
  postal_code: string
}

type TrackMembershipPurchaseEventProps = {
  user_type: 'New' | 'Existing' | 'Guest'
  currency: string
  tax?: number
  shipping?: number
  coupon?: string
  transaction_id: string
  value: number
  shipping_tier?: string
  payment_type: string
  items: MembershipPlanItem[]
  user_data: UserData
  user_email?: string
  checkout_path?: CheckoutPath
}

export const trackMembershipPurchaseEvent = ({
  user_email,
  user_type,
  currency,
  tax,
  shipping,
  coupon,
  transaction_id,
  value,
  shipping_tier,
  payment_type,
  items,
  user_data,
  checkout_path,
}: TrackMembershipPurchaseEventProps) => {
  const trialLength = getTrialLengthFromItems(items)
  const event = {
    event: 'purchase',
    user_type: user_type,
    section: 'checkout',
    page_name: 'purchase',
    ...(checkout_path && { checkout_path }),
    product_type: 'membership',
    product_selected: items[0]?.item_bundle || items[0]?.item_id,
    ...(trialLength && { order_trial_length: trialLength }),
    ecommerce: {
      currency: currency?.toUpperCase(),
      tax,
      coupon,
      transaction_id,
      value,
      payment_type,
      items: mapMembershipItems(items),
      ...(shipping && { shipping }),
      ...(shipping_tier && { shipping_tier }),
    },
    user_data,
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}

type TrackGutHealthTestPurchaseEventProps = {
  user_type: 'New' | 'Existing' | 'Guest'
  currency: string
  lookup_key: string
  tax?: number
  shipping?: number
  coupon?: string
  transaction_id: string
  value: number
  shipping_tier?: string
  payment_type: string
  user_data: UserData
  user_email?: string
  country?: string
  price?: number
  checkout_path?: CheckoutPath
}
export const trackGutHealthTestPurchaseEvent = ({
  user_email,
  currency,
  lookup_key,
  tax,
  shipping,
  coupon,
  transaction_id,
  value,
  shipping_tier,
  payment_type,
  user_data,
  country,
  price,
  checkout_path,
}: TrackGutHealthTestPurchaseEventProps) => {
  const event = {
    event: 'purchase',
    user_type: 'Existing',
    section: 'checkout',
    page_name: 'purchase',
    ...(checkout_path && { checkout_path }),
    product_type: 'tests',
    product_selected: lookup_key,
    ecommerce: {
      currency: currency?.toUpperCase(),
      tax,
      coupon,
      transaction_id,
      value,
      payment_type,
      items: [
        {
          item_id: lookup_key,
          item_name: 'Gut Health Test',
          item_brand: 'ZOE',
          item_category: 'Tests',
          item_category2: 'Gut Health Test',
          item_category3: 'One time payment',
          item_category4: country,
          price: price,
          quantity: 1,
        },
      ],
      ...(shipping && { shipping }),
      ...(shipping_tier && { shipping_tier }),
    },
    user_data,
    ...(user_email && { user_id: hashEmail(user_email) }),
  }

  sendGtmEvent(event)
}
